import React from "react";

export type PaginationMetadata = {
  totalCount: number;
  pageSize: string | number;
  cursor?: string;
  beforeCursor?: string;
};

type PaginationProps = {
  metadata?: PaginationMetadata;
  currentPage: number;
  pageSize: number;
  onPageChange: (pageNumber: number) => void;
  className?: string;
};

const Pagination = ({
  metadata,
  currentPage,
  onPageChange,
  pageSize,
  className = "",
}: PaginationProps) => {
  if (!metadata || metadata.totalCount <= pageSize) {
    console.warn(
      "Pagination component: No metadata or totalCount is less than or equal to pageSize. Pagination will not be rendered.",
    );
    return null;
  }
  const totalPages = Math.max(1, Math.ceil(metadata?.totalCount / pageSize));

  const getPageNumbers = () => {
    if (totalPages <= 1) return [];

    const numbers: (number | string)[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) numbers.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) numbers.push(i);
      numbers.push("...");
      numbers.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      numbers.push(1);
      numbers.push("...");
      for (let i = totalPages - 4; i <= totalPages; i++) numbers.push(i);
    } else {
      numbers.push(1);
      numbers.push("...");
      numbers.push(currentPage - 1);
      numbers.push(currentPage);
      numbers.push(currentPage + 1);
      numbers.push("...");
      numbers.push(totalPages);
    }

    return numbers;
  };

  if (totalPages <= 1) return null;

  const pageNumbers = getPageNumbers();

  const baseBtn =
    "inline-flex items-center justify-center min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium transition-all duration-200 border";

  const activeBtn = "bg-[#2D96A7] text-white border-[#2D96A7] shadow-sm";
  const inactiveBtn =
    "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300";
  const disabledBtn = "opacity-40 cursor-not-allowed pointer-events-none";

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-2 ${className}`}
    >
      {/* Previous */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${baseBtn} ${currentPage <= 1 ? disabledBtn : inactiveBtn}`}
      >
        <svg
          className="w-4 h-4 mr-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Prev
      </button>

      {/* Page numbers */}
      <div className="flex items-center gap-1.5 overflow-x-auto max-w-full py-1">
        {pageNumbers.map((pageNum, idx) =>
          pageNum === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="inline-flex items-center justify-center w-10 h-10 text-gray-400 text-sm"
            >
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum as number)}
              className={`${baseBtn} ${
                currentPage === pageNum ? activeBtn : inactiveBtn
              }`}
            >
              {pageNum}
            </button>
          ),
        )}
      </div>

      {/* Next */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${baseBtn} ${
          currentPage >= totalPages ? disabledBtn : inactiveBtn
        }`}
      >
        Next
        <svg
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

export default Pagination;
